import { API, env } from 'shared/config';

import type { routes } from '@/assets/constants/routes-no.ts';

const isProdEnv = env === 'release';

const DEFAULT_IMAGE = {
  src: '/scooprog.jpeg',
  alt: 'Scoopr',
} as const;

type SEOProps = {
  title: string;
  description: string;
  /** `null` skips the canonical URL. */
  path: (typeof routes)[keyof typeof routes] | null;
  image?: { src: string; alt: string };
  noindex?: boolean;
};

const toAbsoluteUrl = (pathOrUrl: string) => {
  if (/^https?:\/\//.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const origin = API.domain.replace(/\/$/, '');
  const pathname = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;

  return `${origin}${pathname}`;
};

export function SEO({ title, description, path, image, noindex }: SEOProps) {
  const pageTitle = `${title} - Scoopr`;
  const url = path ? toAbsoluteUrl(path) : null;
  const { src, alt } = image ?? DEFAULT_IMAGE;
  const imageUrl = toAbsoluteUrl(src);

  return (
    <>
      {!isProdEnv || noindex ? <meta name="robots" content="noindex" /> : null}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {url ? <link rel="canonical" href={url} /> : null}

      <meta property="og:type" content="website" />
      {url ? <meta property="og:url" content={url} /> : null}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content="nb_NO" />
      <meta property="og:site_name" content="Scoopr" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={alt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={alt} />
    </>
  );
}
